import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material';
import Markdown from '../../ManagementPanel/components/Markdown';
import {
  getPlanPaymentFrequency,
  setPlanPaymentFrequency,
} from '../../../store/policystreamStore';
import { PolicyStreamPlanAmount } from './PolicyStreamPlanAmount';
import { PolicyStreamPlan } from './PolicyStreamPlanSelectionPage';
import { PolicyStreamPlanIcon } from './PolicyStreamPlanIcon';
import { PolicyStreamCard } from '../components';
import { grey } from '@mui/material/colors';
import { useMediaQuery, useTheme } from '@mui/material';
import { CoreQuotePolicystreamPlanPaymentFrequencyChoices as PolicyStreamPlanPaymentFrequencyChoices } from '../../../gql/graphql';

interface DisplayPolicyStreamPlanProps {
  plan: PolicyStreamPlan;
  plans: Array<PolicyStreamPlan>;
  selected: boolean;
  select: () => void;
}
export const DisplayPolicyStreamPlan = ({
  plan,
  plans,
  selected,
  select,
}: DisplayPolicyStreamPlanProps) => {
  const planPaymentFrequency = useSelector(getPlanPaymentFrequency);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <PolicyStreamPlanContainer selected={selected}>
        <PolicyStreamPlanInnerContainer selected={selected}>
          {isMobile ? (
            <PolicyStreamMobilePlanContainer>
              <PolicyStreamMobilePlanContainerTopRow>
                <PolicyStreamPlanIconContainer>
                  <PolicyStreamPlanIcon
                    insurer={plan.insurer}
                    sx={{ width: '100%' }}
                  />
                </PolicyStreamPlanIconContainer>
                <PolicyStreamMobilePlanHeaderContainer>
                  <PolicyStreamAdditionalPlanDetails plan={plan} />
                  <PolicyStreamSelectPlanButton
                    selected={selected}
                    select={select}
                    isOnlyPlan={plans.length === 1}
                  />
                </PolicyStreamMobilePlanHeaderContainer>
              </PolicyStreamMobilePlanContainerTopRow>
              <PolicyStreamPlanText plan={plan} selected={selected} />
            </PolicyStreamMobilePlanContainer>
          ) : (
            <>
              <PolicyStreamPlanIconContainer>
                <PolicyStreamPlanIcon
                  insurer={plan.insurer}
                  sx={{ width: '100%' }}
                />
              </PolicyStreamPlanIconContainer>
              <PolicyStreamPlanText plan={plan} selected={selected} />
              <PlanActionsContainer>
                <PolicyStreamAdditionalPlanDetails plan={plan} />
                <PolicyStreamSelectPlanButton
                  selected={selected}
                  select={select}
                  isOnlyPlan={plans.length === 1}
                />
              </PlanActionsContainer>
            </>
          )}
        </PolicyStreamPlanInnerContainer>
      </PolicyStreamPlanContainer>
      <Collapse in={selected && !planPaymentFrequency}>
        <Typography sx={{ py: 2 }}>
          Please select your payment schedule
        </Typography>
      </Collapse>
    </>
  );
};

const PolicyStreamMobilePlanHeaderContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'right',
    width: '100%',
    gap: theme.spacing(2),
  };
});

const PolicyStreamMobilePlanContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1),
  };
});

const PolicyStreamMobilePlanContainerTopRow = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: 'white',
    padding: theme.spacing(1),
  };
});

const PlanActionsContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: { flexDirection: 'column' },
    alignItems: 'center',
    gap: theme.spacing(2),
  };
});

const PolicyStreamPlanIconContainer = styled(Card)(({ theme }) => {
  return {
    alignSelf: 'stretch',
    maxWidth: '25%',
    backgroundColor: 'white',
    borderRadius: `${theme.shape.borderRadius} 0 0 ${theme.shape.borderRadius}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    marginLeft: 0,
  };
});

interface PolicyStreamAdditionalPlanDetailsProps {
  plan: PolicyStreamPlan;
}
export const PolicyStreamAdditionalPlanDetails = ({
  plan,
}: PolicyStreamAdditionalPlanDetailsProps) => {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  if (!plan.markdownDetails) {
    return null;
  }
  return (
    <>
      <Button
        color='primary'
        size='small'
        onClick={() => setDetailsDialogOpen(true)}
        sx={{ flexShrink: 0, flexGrow: 0, padding: 0.5 }}
        endIcon={<ArrowOutwardIcon />}
      >
        View Details
      </Button>
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
      >
        <DialogTitle>Plan Details</DialogTitle>
        <DialogContent>
          <Markdown>{plan.markdownDetails}</Markdown>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface PolicyStreamPlanContainerProps {
  selected: boolean;
}
const PolicyStreamPlanContainer = styled(PolicyStreamCard, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<PolicyStreamPlanContainerProps>(({ theme, selected }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    boxShadow: selected
      ? `0 0 ${theme.spacing(1)} rgba(0, 0, 0, 0.8)`
      : undefined,
    marginBottom: selected ? theme.spacing(1) : 0,
    marginTop: selected ? theme.spacing(1) : 0,
    paddingLeft: 0,
    width: '100%',
  };
});

interface PolicyStreamPlanInnerContainerProps {
  selected: boolean;
}
const PolicyStreamPlanInnerContainer = styled(PolicyStreamCard, {
  shouldForwardProp: (prop) => prop !== 'selected',
  // @ts-expect-error
})<PolicyStreamPlanInnerContainerProps>(({ theme, selected }) => {
  return {
    backgroundColor: grey[200],
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 0,
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      paddingRight: 0,
      width: '100%',
    },
  };
});

interface PolicyStreamSelectPlanButtonProps
  extends Pick<DisplayPolicyStreamPlanProps, 'select' | 'selected'> {
  isOnlyPlan: boolean;
}
const PolicyStreamSelectPlanButton = ({
  selected,
  select,
  isOnlyPlan,
}: PolicyStreamSelectPlanButtonProps) => {
  const dispatch = useDispatch();
  return (
    <PolicyStreamCard
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Button
        variant={selected ? 'contained' : 'text'}
        color='primary'
        disabled={isOnlyPlan}
        onClick={() => {
          select();
          dispatch(
            setPlanPaymentFrequency(
              PolicyStreamPlanPaymentFrequencyChoices.Monthly,
            ),
          );
        }}
        sx={{
          width: '12ch',
        }} // 12 character widths
      >
        {selected ? 'Selected' : 'Select'}
      </Button>
    </PolicyStreamCard>
  );
};

interface PolicyStreamPlanTextProps {
  plan: PolicyStreamPlan;
  selected: boolean;
}
const PolicyStreamPlanText = ({
  plan,
  selected,
}: PolicyStreamPlanTextProps) => {
  return (
    <PolicyStreamPlanTextContainer>
      <PolicyStreamPlanAmount plan={plan} selected={selected} />
    </PolicyStreamPlanTextContainer>
  );
};

const PolicyStreamPlanTextContainer = styled('div')({
  padding: 2,
  borderRadius: 2,
  flexGrow: 1,
});
